import React, { useCallback, useMemo } from 'react';
import { patchSongCollection, SongCollection, uploadSongCollectionImage } from '@round/api';
import Modal, { ModalContent, ModalTitle } from '../../../../ui/Layout/Modals/Modal/Modal';
import SongCollectionForm, { SongCollectionFormProps } from '../../components/SongCollectionForm/SongCollectionForm';
import useDownloadImage from '../../../../Hooks/useDownloadImage';
import useNonNullContext from '../../../../Hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/Options';
import { showNotification } from '../../../../helpers';

type EditSongCollectionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onUpdated: (collection: SongCollection) => void;
    songCollection: SongCollection | null;
};

const EditSongCollectionModal = ({ onUpdated, onClose, isOpen, songCollection }: EditSongCollectionModalProps) => {
    const { clients } = useNonNullContext(OptionsContext);
    const { image } = useDownloadImage(songCollection?.picture);

    const initialValues: SongCollectionFormProps['initialValues'] = useMemo(() => {
        const client = clients.find((c) => songCollection?.client === c.id);

        return {
            client: client ? { value: client.id, label: client.name } : null,
            name: songCollection?.name ?? '',
            image: image ? [image] : [],
        };
    }, [clients, image, songCollection?.client, songCollection?.name]);

    const handleSubmit: SongCollectionFormProps['handleSubmit'] = useCallback(
        async (values, { setSubmitting }) => {
            if (!values.client || !songCollection) {
                return;
            }

            try {
                setSubmitting(true);
                const response = await patchSongCollection(songCollection.id, {
                    name: values.name,
                    client: values.client.value,
                });

                if (response.status === 404) {
                    showNotification(response.data.detail, 'error');
                    return;
                }

                const [image] = values.image;
                if (!image) {
                    onUpdated(response.data);
                    showNotification('Song collection updated', 'info');
                    return;
                }

                const imageResponse = await uploadSongCollectionImage(songCollection.id, image);
                if (imageResponse.status === 404) {
                    showNotification(imageResponse.data.detail, 'error');
                    return;
                }

                onUpdated(imageResponse.data);
                showNotification('Song collection updated', 'info');
            } catch {
                showNotification('Could not update song collection', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [onUpdated, songCollection]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <ModalTitle>Edit Song Collection</ModalTitle>
            <ModalContent>
                <SongCollectionForm initialValues={initialValues} handleSubmit={handleSubmit} />
            </ModalContent>
        </Modal>
    );
};

export default EditSongCollectionModal;
